import React, { useEffect, useRef, useState } from 'react'
import SlickSlider from 'react-slick'
import { forwardRef } from 'react'
import {
  ArrowNextStyled,
  ArrowPrevStyled,
  SliderWrapper,
  StyledCarouselBanner,
  TouchScroller
} from './Carousel.styles'
import type { ICarouselProps } from './interface'
import { FontIcon, tokens } from '@arvoreeducacao/bonsai-ds'
import { post_event } from 'utils/analytics'
import { useTranslation } from 'i18n'

const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'

const TOUCH_SCROLL_QUERY = [
  `(max-width: ${tokens.breakpoints.tablet}px) and (pointer: coarse)`,
  `(max-width: ${tokens.sizes.smScreen}px)`
].join(', ')

function matchesTouchScrollViewport() {
  if (typeof window === 'undefined') return false
  if (typeof window.matchMedia !== 'function') return false

  return window.matchMedia(TOUCH_SCROLL_QUERY).matches
}

function useTouchScrollViewport() {
  const [isTouchScrollViewport, setIsTouchScrollViewport] = useState(
    matchesTouchScrollViewport
  )

  useEffect(() => {
    if (typeof window === 'undefined') return
    if (typeof window.matchMedia !== 'function') return

    const query = window.matchMedia(TOUCH_SCROLL_QUERY)
    if (typeof query.addEventListener !== 'function') return

    const syncViewport = () => setIsTouchScrollViewport(query.matches)

    syncViewport()
    query.addEventListener('change', syncViewport)

    return () => query.removeEventListener('change', syncViewport)
  }, [])

  return isTouchScrollViewport
}

function useNativeLazyImages(enabled: boolean) {
  const scrollerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!enabled) return

    const scroller = scrollerRef.current
    if (!scroller) return

    scroller.querySelectorAll('img').forEach(image => {
      if (!image.hasAttribute('loading')) {
        image.setAttribute('loading', 'lazy')
      }
    })
  })

  return scrollerRef
}

function eventName(module, name) {
  return module ? `${name}/ClickCarrossel/${module}` : `${name}/ClickCarrossel`
}

function setAttributeIfNeeded(element: Element, name: string, value: string) {
  if (element.getAttribute(name) !== value) {
    element.setAttribute(name, value)
  }
}

function removeAttributeIfNeeded(element: Element, name: string) {
  if (element.hasAttribute(name)) {
    element.removeAttribute(name)
  }
}

function useInertOffscreenSlides(enabled: boolean) {
  const wrapperRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!enabled) return

    const wrapper = wrapperRef.current
    if (!wrapper) return
    if (typeof IntersectionObserver === 'undefined') return

    const list = wrapper.querySelector('.slick-list')
    if (!list) return

    const onScreenSlides = new Set<Element>()
    let hasMeasured = false

    const applyVisibility = () => {
      const slides = wrapper.querySelectorAll('.slick-slide')
      let focusWasTrapped = false

      slides.forEach(slide => {
        if (onScreenSlides.has(slide)) {
          setAttributeIfNeeded(slide, 'aria-hidden', 'false')
          removeAttributeIfNeeded(slide, 'inert')
          return
        }

        if (slide.contains(document.activeElement)) {
          focusWasTrapped = true
        }

        setAttributeIfNeeded(slide, 'aria-hidden', 'true')
        setAttributeIfNeeded(slide, 'inert', '')
      })

      if (!focusWasTrapped) return

      for (let index = 0; index < slides.length; index += 1) {
        const slide = slides[index]
        if (!onScreenSlides.has(slide)) continue

        const focusable = slide.querySelector<HTMLElement>(FOCUSABLE_SELECTOR)
        if (focusable) {
          focusable.focus()
          return
        }
      }
    }

    const intersectionObserver = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            onScreenSlides.add(entry.target)
          } else {
            onScreenSlides.delete(entry.target)
          }
        })

        hasMeasured = true
        applyVisibility()
      },
      { root: list, threshold: 0 }
    )

    const observeSlides = () => {
      wrapper.querySelectorAll('.slick-slide').forEach(slide => {
        intersectionObserver.observe(slide)
      })
    }

    observeSlides()

    const mutationObserver = new MutationObserver(mutations => {
      if (mutations.some(mutation => mutation.type === 'childList')) {
        observeSlides()
      }

      if (!hasMeasured) return

      applyVisibility()
    })

    mutationObserver.observe(wrapper, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ['aria-hidden', 'class']
    })

    return () => {
      intersectionObserver.disconnect()
      mutationObserver.disconnect()
    }
  }, [enabled])

  return wrapperRef
}

function ArrowNext(props) {
  const { onClick, className, module, name } = props
  const { t } = useTranslation()
  const isDisabled = String(className || '').includes('slick-disabled')

  const advance = event => {
    if (isDisabled) return
    post_event(eventName(module, name), 'TRUE')
    onClick(event)
  }

  return (
    <ArrowNextStyled
      className={className}
      role="button"
      tabIndex={isDisabled ? -1 : 0}
      aria-label={t('Avançar')}
      aria-disabled={isDisabled || undefined}
      onKeyDown={event => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault()
          advance(event)
        }
      }}
      onClick={advance}
    >
      <FontIcon iconName="caret-right" color={tokens.colors.dark5} />
    </ArrowNextStyled>
  )
}

function ArrowPrev(props) {
  const { onClick, className, module, name } = props
  const { t } = useTranslation()
  const isDisabled = String(className || '').includes('slick-disabled')

  const goBack = event => {
    if (isDisabled) return
    post_event(eventName(module, name), 'TRUE')
    onClick(event)
  }

  return (
    <ArrowPrevStyled
      className={className}
      role="button"
      tabIndex={isDisabled ? -1 : 0}
      aria-label={t('Voltar')}
      aria-disabled={isDisabled || undefined}
      onKeyDown={event => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault()
          goBack(event)
        }
      }}
      onClick={goBack}
    >
      <FontIcon iconName="caret-left" color={tokens.colors.dark5} />
    </ArrowPrevStyled>
  )
}

const Carousel: React.ForwardRefRenderFunction<SlickSlider, ICarouselProps> = (
  { children, settings, banner, spaceBetweenItems, contentFilter, title },
  ref
) => {
  const isTouchScrollViewport = useTouchScrollViewport()
  const usesTouchScroll = !banner && isTouchScrollViewport
  const wrapperRef = useInertOffscreenSlides(!usesTouchScroll)
  const scrollerRef = useNativeLazyImages(usesTouchScroll)

  if (usesTouchScroll) {
    return (
      <TouchScroller
        data-testid="default"
        spaceBetweenItems={spaceBetweenItems}
        ref={scrollerRef}
      >
        {children}
      </TouchScroller>
    )
  }

  if (banner) {
    return (
      <StyledCarouselBanner data-testid="banner" ref={wrapperRef}>
        <SlickSlider
          ref={ref}
          {...settings}
          nextArrow={<ArrowNext name="Banner" />}
          prevArrow={<ArrowPrev name="Banner" />}
        >
          {children}
        </SlickSlider>
      </StyledCarouselBanner>
    )
  }

  return (
    <SliderWrapper
      data-testid="default"
      spaceBetweenItems={spaceBetweenItems}
      ref={wrapperRef}
    >
      <SlickSlider
        ref={ref}
        {...settings}
        nextArrow={<ArrowNext module={contentFilter} name={title} />}
        prevArrow={<ArrowPrev module={contentFilter} name={title} />}
      >
        {children}
      </SlickSlider>
    </SliderWrapper>
  )
}

export default forwardRef(Carousel)
